import { yupResolver } from "@hookform/resolvers/yup"
import { Stack, Typography } from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import { useWatch, type Resolver } from "react-hook-form"
import * as yup from "yup"

import ControlledSwitch from "@components/controlled_switch"
import DialogProvider from "@components/dialog_provider"
import MultiSelectAsync from "@components/multiselect_input/sub_components/async_paginate"
import TextInput from "@components/text_input"
import { useCustomerPageContext } from "@contexts/customer"
import {
  accountCreateCustomerBillingAddresses,
  getCountries,
  getLocations
} from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { showErrorToast } from "@utils/toast"
import {
  Country,
  CustomerBillingAddressesInput,
  StateProvinceCity
} from "__generated__/graphql"

export type BillingInformationData = Omit<
  CustomerBillingAddressesInput,
  "customer"
>

export type OptionType = {
  value: string
  label: string
}

export type loadOptionData = {
  options: { value: string; label: string }[]
  hasMore: boolean
}

export type BillingInformationProps = {
  billingInformation: BillingInformationData | undefined
  setBillingInformation: (value: BillingInformationData | undefined) => void
  isAddBillingInformationModalOpen: boolean
  toggleIsAddBillingInformationModalOpen: () => void
  customerId?: string
}

export default function BillingInformation({
  billingInformation,
  setBillingInformation,
  isAddBillingInformationModalOpen,
  toggleIsAddBillingInformationModalOpen,
  customerId
}: BillingInformationProps) {
  const { translate, me } = useCustomerPageContext()
  /* const { fromQuery, paymentQuery, toggleIsAddCardModalOpen } =
    useAccountCurrentPlanPageContext() */
  const [loading, setLoading] = useState(false)

  const [selectedCountry, setSelectedCountry] = useState<
    Country & { value?: string }
  >()

  const [selectedProvince, setSelectedProvince] = useState<
    StateProvinceCity & { value?: string }
  >()

  const [selectedCity, setSelectedCity] = useState<StateProvinceCity>()

  const schema = yup
    .object({
      country: yup.string().required(
        translate("modal_input_is_required", {
          name: translate("country")
        })
      ),
      state: yup.string().nullable(),
      province: yup.string().nullable(),
      city: yup.string().required(
        translate("modal_input_is_required", {
          name: translate("city")
        })
      ),
      postCode: yup.string().required(
        translate("modal_input_is_required", {
          name: translate("zip_code")
        })
      ),
      address1: yup.string().required(
        translate("modal_input_is_required", {
          name: translate("address_line", {
            number: "1"
          })
        })
      ),
      address2: yup.string().nullable(),
      accountingContactId: yup.string().nullable(),
      isCompany: yup.boolean(),
      legalCompanyName: yup
        .string()
        .when("isCompany", ([isCompany], schema) => {
          return isCompany === true
            ? schema.required(
                translate("modal_input_is_required", {
                  name: translate("company_legal_name")
                })
              )
            : schema.notRequired()
        }),

      taxOffice: yup.string().when("isCompany", ([isCompany], schema) => {
        return isCompany === true
          ? schema.required(
              translate("modal_input_is_required", {
                name: translate("tax_city_office")
              })
            )
          : schema.notRequired()
      }),

      taxNumber: yup.string().when("isCompany", ([isCompany], schema) => {
        return isCompany === true
          ? schema
              .min(
                5,
                translate("dynamic_at_least_characters", {
                  name: translate("tax_number"),
                  value: 5
                })
              )
              .required(
                translate("modal_input_is_required", {
                  name: translate("tax_number")
                })
              )
          : schema.notRequired()
      })
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty }
  } = useControlledForm<BillingInformationData>({
    resolver: yupResolver(schema) as Resolver<BillingInformationData>,
    mode: "all",
    defaultValues: {
      isCompany: true
    }
  })
  const billingForCompany = useWatch({
    control,
    name: "isCompany"
  })
  useEffect(() => {
    reset(billingInformation)
  }, [billingInformation])

  function handleClose() {
    if (loading) {
      return
    }
    toggleIsAddBillingInformationModalOpen?.()
    reset()
  }

  async function onSubmit(data: BillingInformationData) {
    if (!me || !customerId) return
    setLoading(true)
    const { isSuccess, errorMessage } =
      await accountCreateCustomerBillingAddresses({
        accountingContactId: data.accountingContactId?.trim() || null,
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        country: data.country,
        customer: customerId,
        isCompany: data.isCompany,
        legalCompanyName: data.isCompany ? data.legalCompanyName : "",
        postCode: data.postCode,
        province: data.province,
        state: data.state,
        taxNumber: data.isCompany ? data.taxNumber : "",
        taxOffice: data.isCompany ? data.taxOffice : ""
      })
    if (!isSuccess) {
      setLoading(false)
      showErrorToast(String(errorMessage))
      return { options: [], hasMore: false }
    }
    setBillingInformation?.({
      ...data,
      taxNumber: data.isCompany ? data.taxNumber : "",
      taxOffice: data.isCompany ? data.taxOffice : "",
      legalCompanyName: data.isCompany ? data.legalCompanyName : ""
    })
    setLoading(false)
    handleClose()
    /* if (fromQuery === "phone" && paymentQuery === "false") {
      toggleIsAddCardModalOpen?.()
    } */
  }

  const CompanyInformations = useMemo(() => {
    if (!billingForCompany) return
    return (
      <>
        <TextInput<BillingInformationData>
          type="text"
          name="legalCompanyName"
          control={control}
          label={translate("company_legal_name")}
          placeholder={translate("enter")}
          disabled={loading}
          required={billingForCompany}
        />
        <Stack direction={"row"} gap={1}>
          <TextInput<BillingInformationData>
            type="text"
            name="taxOffice"
            control={control}
            label={translate("tax_city_office")}
            placeholder={translate("enter")}
            disabled={loading}
            required={billingForCompany}
          />
          <TextInput<BillingInformationData>
            type="text"
            name="taxNumber"
            control={control}
            label={translate("tax_number")}
            placeholder={translate("enter")}
            disabled={loading}
            required={billingForCompany}
          />
        </Stack>
      </>
    )
  }, [billingForCompany])

  const [countryLoading, setCountryLoading] = useState(false)
  const [provinceLoading, setProvinceLoading] = useState(false)
  const [cityLoading, setCityLoading] = useState(false)

  const country = useWatch({
    control,
    name: "country"
  })
  const province = useWatch({
    control,
    name: "province"
  })
  const state = useWatch({
    control,
    name: "state"
  })
  const city = useWatch({
    control,
    name: "city"
  })
  useEffect(() => {
    if (selectedCity?.stateProvince) {
      setValue("province", selectedCity?.stateProvince?.name)
    }
  }, [selectedCity, setValue])
  useEffect(() => {
    if (selectedCountry?.value) {
      setValue("province", "")
      setValue("state", "")
      setValue("city", "")
      setSelectedProvince?.({})
    }
  }, [selectedCountry?.value])
  useEffect(() => {
    if (selectedProvince?.value) {
      setValue("city", "")
    }
  }, [selectedProvince?.value])
  const [countryPage, setCountryPage] = useState(1)
  const [provincePage, setProvincePage] = useState(1)
  const [cityPage, setCityPage] = useState(1)
  const [prevCountrySearch, setCountryPrevSearch] = useState("")
  const [prevProvinceSearch, setProvincePrevSearch] = useState("")
  const [prevCitySearch, setCityPrevSearch] = useState("")

  useEffect(() => {
    setCountryPage(1)
    setCountryPrevSearch("")
    setProvincePage(1)
    setProvincePrevSearch("")
    setCityPage(1)
    setCityPrevSearch("")
  }, [toggleIsAddBillingInformationModalOpen])
  async function getExistedCountry() {
    const { isSuccess: countryIsSuccess, data: countryData } =
      await getCountries({
        search: country,
        pageNumber: 1,
        pageSize: 1
      })
    if (!countryIsSuccess) {
      setCountryLoading(false)
      return { options: [], hasMore: false }
    }
    const countryDataDoc = countryData?.docs?.[0]
    if (!countryDataDoc) {
      setCountryLoading(false)
      return { options: [], hasMore: false }
    }
    setSelectedCountry?.(countryDataDoc)
    const { isSuccess: provinceIsSuccess, data: provinceData } =
      await getLocations({
        pageNumber: 1,
        pageSize: 1,
        search: province,
        countryId: countryDataDoc._id,
        type: countryDataDoc.hasProvince
          ? "province"
          : countryDataDoc.hasState
            ? "state"
            : undefined
      })
    if (!provinceIsSuccess) {
      setLoading(false)
      return { options: [], hasMore: false }
    }
    const provinceDataDoc = provinceData?.docs?.[0]
    if (!provinceDataDoc) {
      setCountryLoading(false)
      return { options: [], hasMore: false }
    }
    setSelectedProvince?.(provinceDataDoc)
    const { isSuccess: cityIsSuccess, data: cityData } = await getLocations({
      pageNumber: 1,
      pageSize: 1,
      search: city,
      countryId: countryDataDoc._id,
      stateProvinceId: provinceDataDoc?._id,
      type: "city"
    })
    if (!cityIsSuccess) {
      setLoading(false)
      return { options: [], hasMore: false }
    }
    const cityDataDoc = cityData?.docs?.[0]
    if (!cityDataDoc) {
      setCountryLoading(false)
      return { options: [], hasMore: false }
    }
    setSelectedCity?.(cityDataDoc)
  }

  useEffect(() => {
    if (country && province && city) {
      getExistedCountry()
    }
  }, [isAddBillingInformationModalOpen])

  async function loadCountries(search: string): Promise<loadOptionData> {
    if (!me) return { options: [], hasMore: false }

    if (search !== prevCountrySearch) {
      setCountryPage(1)
      setCountryPrevSearch(search)
    }
    setCountryLoading(true)

    const { isSuccess, data } = await getCountries({
      pageNumber: !search || search === prevCountrySearch ? countryPage : 1,
      pageSize: 10,
      search: search
    })

    if (!isSuccess) {
      setCountryLoading(false)
      return { options: [], hasMore: false }
    }

    const countries = data?.docs
      ?.map((doc) => {
        if (!doc || !me) return undefined
        return {
          /* value:
            doc.translations?.[
              me?.language?.toLowerCase() as keyof typeof doc.translations
            ] || doc.name, */
          value: doc.name,
          /*   label:
            doc.translations?.[
              me?.language?.toLowerCase() as keyof typeof doc.translations
            ] || doc.name, */
          label: doc.name,
          hasState: doc.hasState,
          hasProvince: doc.hasProvince,
          hasCity: doc.hasCity,
          _id: doc._id
        }
      })
      .filter(Boolean) as loadOptionData["options"]

    setCountryPage((prevPage) => prevPage + 1)
    setCountryLoading(false)

    return {
      options: countries,
      hasMore: countries.length >= 10
    }
  }

  async function loadProvinces(search: string): Promise<loadOptionData> {
    if (!me) return { options: [], hasMore: false }
    if (search !== prevProvinceSearch) {
      setProvincePage(1)
      setProvincePrevSearch(search)
    }
    setProvinceLoading(true)
    const { isSuccess, data } = await getLocations({
      pageNumber: !search || search === prevProvinceSearch ? provincePage : 1,
      pageSize: 10,
      search: search,
      countryId: selectedCountry?._id || undefined,
      type: selectedCountry?.hasProvince
        ? "province"
        : selectedCountry?.hasState
          ? "state"
          : undefined
    })
    if (!isSuccess) {
      setLoading(false)
      return { options: [], hasMore: false }
    }
    const countries = data?.docs
      ?.map((doc) => {
        if (!doc) return undefined
        return {
          value: doc.name,
          label: doc.stateProvince
            ? `${doc.stateProvince.name} / ${doc.name}`
            : doc.name,
          _id: doc._id,
          stateProvince: doc.stateProvince,
          __typename: doc.__typename
        }
      })
      .filter(Boolean) as loadOptionData["options"]
    setProvincePage((prevPage) => prevPage + 1)
    setProvinceLoading(false)
    return {
      options: countries,
      hasMore: countries.length >= 10
    }
  }
  async function loadCities(search: string): Promise<loadOptionData> {
    if (!me) return { options: [], hasMore: false }
    if (search !== prevCitySearch) {
      setCityPage(1)
      setCityPrevSearch(search)
    }
    setCityLoading(true)
    const { isSuccess, data } = await getLocations({
      pageNumber: !search || search === prevCitySearch ? cityPage : 1,
      pageSize: 10,
      search: search,
      countryId: selectedCountry?._id || undefined,
      stateProvinceId: selectedProvince?._id || undefined,
      type: "city"
    })
    if (!isSuccess) {
      setLoading(false)
      return { options: [], hasMore: false }
    }
    const countries = data?.docs
      ?.map((doc) => {
        if (!doc) return undefined
        return {
          value: doc.name,
          label: doc.stateProvince
            ? `${doc.name} / ${doc.stateProvince.name} `
            : doc.name,
          stateProvince: doc.stateProvince
        }
      })
      .filter(Boolean) as loadOptionData["options"]
    setCityPage((prevPage) => prevPage + 1)
    setCityLoading(false)
    return {
      options: countries,
      hasMore: countries.length >= 10
    }
  }
  const [cacheUniqsCountry, setCacheUniqsCountry] = useState([country])
  function onMenuCloseCountry() {
    setCountryPage(1)
    setCountryPrevSearch("")
    //todo: take a look at the backend
    setCacheUniqsCountry([`${country}${Math.random()}`])
  }
  const [cacheUniqsProvince, setCacheUniqsProvince] = useState([province || ""])
  function onMenuCloseProvince() {
    setProvincePage(1)
    setProvincePrevSearch("")
    setCacheUniqsProvince([`${province}${Math.random()}`])
  }
  const [cacheUniqsCity, setCacheUniqsCity] = useState([city])
  function onMenuCloseCity() {
    setCityPage(1)
    setCityPrevSearch("")
    //todo: take a look at the backend
    setCacheUniqsCity([`${city}${Math.random()}`])
  }
  const MemorizedProvinceState = useMemo(() => {
    if (selectedCountry?.hasState) {
      return (
        <MultiSelectAsync<BillingInformationData>
          name="state"
          control={control}
          label={translate("state")}
          placeholder={translate("enter")}
          disabled={loading}
          isLoading={provinceLoading}
          closeMenuOnSelect={true}
          loadOptions={loadProvinces}
          isMulti={false}
          getSelectedOption={setSelectedProvince}
          defaultValue={[{ value: state || "", label: state || "" }]}
          value={{ value: state || "", label: state || "" }}
          cacheUniqs={cacheUniqsProvince}
          onMenuClose={onMenuCloseProvince}
          required
        />
      )
    } else {
      return (
        <MultiSelectAsync<BillingInformationData>
          name="province"
          control={control}
          label={translate("province")}
          placeholder={translate("enter")}
          disabled={loading}
          isLoading={provinceLoading}
          closeMenuOnSelect={true}
          loadOptions={loadProvinces}
          isMulti={false}
          getSelectedOption={setSelectedProvince}
          cacheUniqs={cacheUniqsProvince}
          onMenuClose={onMenuCloseProvince}
          defaultValue={[{ value: province || "", label: province || "" }]}
          value={{ value: province || "", label: province || "" }}
          required
        />
      )
    }
  }, [
    selectedCountry?.hasState,
    control,
    translate,
    loading,
    provinceLoading,
    setSelectedProvince,
    state,
    cacheUniqsProvince,
    onMenuCloseProvince,
    province
  ])

  return (
    <DialogProvider
      title={translate("billing_information")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: !isValid || !isDirty
      }}
      isOpen={!!isAddBillingInformationModalOpen}
      //   setClose={fromQuery === "phone" ? () => {} : handleClose}
      setClose={handleClose}
      size="large"
    >
      <Stack spacing={2}>
        <Stack direction={"row"} gap={1}>
          <MultiSelectAsync<BillingInformationData>
            name="country"
            control={control}
            label={translate("country")}
            placeholder={translate("modal_select_empty")}
            disabled={loading}
            isLoading={countryLoading}
            closeMenuOnSelect={true}
            loadOptions={loadCountries}
            isMulti={false}
            getSelectedOption={setSelectedCountry}
            defaultValue={[
              {
                value: country || "",
                label: country || ""
              }
            ]}
            value={{
              value: country || "",
              label: country || ""
            }}
            //todo: take a look at the backend
            //@ts-expect-error asd
            cacheUniqs={cacheUniqsCountry}
            onMenuClose={onMenuCloseCountry}
            required
          />
          {MemorizedProvinceState}
        </Stack>
        <Stack direction={"row"} gap={1}>
          <MultiSelectAsync<BillingInformationData>
            name="city"
            getSelectedOption={setSelectedCity}
            control={control}
            label={translate("city")}
            placeholder={translate("enter")}
            disabled={loading}
            isLoading={cityLoading}
            closeMenuOnSelect={true}
            loadOptions={loadCities}
            isMulti={false}
            defaultValue={[
              {
                value: city || "",
                label: city || ""
              }
            ]}
            value={{
              value: city || "",
              label: city || ""
            }}
            //@ts-expect-error asd
            cacheUniqs={cacheUniqsCity}
            onMenuClose={onMenuCloseCity}
            required
          />
          <TextInput<BillingInformationData>
            type="text"
            name="postCode"
            control={control}
            label={translate("zip_code")}
            placeholder={translate("enter")}
            disabled={loading}
            required
          />
        </Stack>
        <TextInput<BillingInformationData>
          type="text"
          name="address1"
          control={control}
          label={translate("address_line", {
            number: "1"
          })}
          placeholder={translate("enter")}
          disabled={loading}
          required
        />
        <TextInput<BillingInformationData>
          type="text"
          name="address2"
          control={control}
          label={translate("address_line", {
            number: "2"
          })}
          placeholder={translate("enter")}
          disabled={loading}
        />
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography variant="subtitle1">
            {translate("billing_for_a_company")}
          </Typography>
          <ControlledSwitch
            control={control}
            name="isCompany"
            color="success"
            disabled={true}
          />
        </Stack>
        {CompanyInformations}
        <TextInput<BillingInformationData>
          type="text"
          name="accountingContactId"
          control={control}
          label={translate("parasut_id")}
          placeholder={translate("enter")}
          disabled={loading}
        />
      </Stack>
    </DialogProvider>
  )
}
